import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Product Tabs Component
 * Handles tab switching and variant-based content visibility
 *
 * @extends Component
 */
class ProductTabsComponent extends Component {
  #abortController = new AbortController();
  #tabs = [];
  #tabContents = [];
  #activeTabIndex = 0;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Initialize tabs
    this.#initializeTabs();

    // Listen for variant change events
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Set initial variant type based on current URL or form
    this.#updateVariantType();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Initialize tab navigation and content
   */
  #initializeTabs() {
    const tabsList = this.querySelector('.tabs');
    const tabsContentList = this.querySelector('.tabs-content');

    if (!tabsList || !tabsContentList) return;

    this.#tabs = Array.from(tabsList.querySelectorAll('li'));
    this.#tabContents = Array.from(tabsContentList.querySelectorAll('li'));

    // Add click handlers to tabs
    this.#tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.#switchTab(index));

      // Make tabs keyboard accessible
      tab.setAttribute('role', 'tab');
      tab.setAttribute('tabindex', index === 0 ? '0' : '-1');
      tab.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
    });

    // Set up tab content panels
    this.#tabContents.forEach((content, index) => {
      content.setAttribute('role', 'tabpanel');
      content.setAttribute('aria-labelledby', `tab-${index}`);
    });

    // Set first tab as active
    this.#switchTab(0);
  }

  /**
   * Switch to a specific tab
   * @param {number} index - The index of the tab to activate
   */
  #switchTab(index) {
    if (index === this.#activeTabIndex) return;

    // Update active tab
    this.#tabs.forEach((tab, i) => {
      const isActive = i === index;
      tab.classList.toggle('active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    // Update active content
    this.#tabContents.forEach((content, i) => {
      content.classList.toggle('active', i === index);
    });

    this.#activeTabIndex = index;
  }

  /**
   * Update variant type based on variant name
   * @param {Object} variant - The variant object
   */
  #updateVariantTypeFromVariant(variant) {
    if (!variant || !variant.name) {
      // Default to "one" if no variant
      this.setAttribute('data-variant-type', 'one');
      return;
    }

    const variantName = variant.name.toLowerCase();

    if (variantName.includes('1 travel router')) {
      this.setAttribute('data-variant-type', 'one');
    } else if (variantName.includes('2 travel router')) {
      this.setAttribute('data-variant-type', 'two');
    } else {
      // Default to "one" if variant name doesn't match either pattern
      this.setAttribute('data-variant-type', 'one');
    }
  }

  /**
   * Update variant type from current form state
   */
  #updateVariantType() {
    // Try to get variant from product form
    const productForm = this.closest('.shopify-section')?.querySelector('product-form-component');
    const variantIdInput = productForm?.querySelector('input[name="id"]');

    if (!variantIdInput?.value) {
      // No variant selected, default to "one"
      this.setAttribute('data-variant-type', 'one');
      return;
    }

    // Get variant data from the select element or data attributes
    const variantPicker = productForm?.querySelector('variant-picker-component');
    const option = variantPicker?.querySelector(`option[value="${variantIdInput.value}"]`);

    if (option) {
      const variantName = option.textContent.toLowerCase();
      if (variantName.includes('1 travel router')) {
        this.setAttribute('data-variant-type', 'one');
      } else if (variantName.includes('2 travel router')) {
        this.setAttribute('data-variant-type', 'two');
      } else {
        // Default to "one" if variant name doesn't match either pattern
        this.setAttribute('data-variant-type', 'one');
      }
    } else {
      // Default to "one" if option not found
      this.setAttribute('data-variant-type', 'one');
    }
  }

  /**
   * Handle variant update event
   * @param {CustomEvent} event - The variant update event
   */
  #onVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (variant) {
      this.#updateVariantTypeFromVariant(variant);
    }
  };

  /**
   * Handle variant selected event
   * @param {CustomEvent} event - The variant selected event
   */
  #onVariantSelected = (event) => {
    const variant = event.detail?.resource;
    if (variant) {
      this.#updateVariantTypeFromVariant(variant);
    }
  };
}

if (!customElements.get('product-tabs-component')) {
  customElements.define('product-tabs-component', ProductTabsComponent);
}
